/**
 * useDynamicAreaDetection - Hook to detect which area user is viewing when panning
 *
 * When border is disabled, this hook tracks map movements and calls bestFit API
 * to determine which area the user is currently viewing. Only calls bestFit after
 * the first map movement to avoid unnecessary API calls on initial load.
 *
 * Prefers city-level areas over neighborhoods: If bestFit returns a neighborhood,
 * we use reverseGeocode to find the parent city area instead.
 */

import { useEffect, useRef, useState } from 'react';
import { useAreaBestFit } from '@/src/lib/hooks/area';
import { type AreaResponse, reverseGeocode } from '@/src/lib/api/endpoints/area';
import type { MapBounds } from '@/src/components/features/search/Map/types';

export interface UseDynamicAreaDetectionParams {
  mapBounds: MapBounds | null;
  borderEnabled: boolean;
  initialArea: AreaResponse | null;
}

export interface UseDynamicAreaDetectionResult {
  detectedArea: AreaResponse | undefined;
  isDetecting: boolean;
  hasMovedOnce: boolean;
}

/**
 * Detects which area user is viewing when panning the map without border
 *
 * @param params - Map bounds, border state, and initial area
 * @returns Detected area, loading state, and movement tracking
 *
 * @example
 * ```tsx
 * const { detectedArea, isDetecting, hasMovedOnce } = useDynamicAreaDetection({
 *   mapBounds,
 *   borderEnabled: showBorder,
 *   initialArea: areaData,
 * });
 *
 * // Use detected area for display
 * const effectiveArea = showBorder ? areaData : (detectedArea ?? areaData);
 * ```
 */
export function useDynamicAreaDetection(
  params: UseDynamicAreaDetectionParams
): UseDynamicAreaDetectionResult {
  const { mapBounds, borderEnabled } = params;

  // Track whether we've seen at least one map movement while border is OFF
  const [hasMovedOnce, setHasMovedOnce] = useState(false);

  // Store initial bounds to detect when map actually moves
  const initialBoundsRef = useRef<MapBounds | null>(mapBounds);

  // Store the resolved city area (when bestFit returns a neighborhood)
  const [resolvedCityArea, setResolvedCityArea] = useState<AreaResponse | undefined>(undefined);

  // When border is toggled ON, reset the movement tracking (but keep detectedArea)
  useEffect(() => {
    if (borderEnabled) {
      setHasMovedOnce(false);
      initialBoundsRef.current = mapBounds;
      // Don't clear resolvedCityArea - we want to preserve it when border is ON
    }
  }, [borderEnabled, mapBounds]);

  // Detect when map bounds have changed from initial bounds
  useEffect(() => {
    if (borderEnabled || !mapBounds) return;

    const boundsChanged =
      initialBoundsRef.current === null ||
      mapBounds.northLat !== initialBoundsRef.current.northLat ||
      mapBounds.southLat !== initialBoundsRef.current.southLat ||
      mapBounds.eastLng !== initialBoundsRef.current.eastLng ||
      mapBounds.westLng !== initialBoundsRef.current.westLng;

    if (boundsChanged && !hasMovedOnce) {
      setHasMovedOnce(true);
    }
  }, [mapBounds, borderEnabled, hasMovedOnce]);

  // Only call bestFit when: border is OFF + map has moved at least once
  const shouldFetchBestFit = !borderEnabled && hasMovedOnce && mapBounds !== null;

  const { data, isLoading } = useAreaBestFit(shouldFetchBestFit ? mapBounds : null, {
    enabled: shouldFetchBestFit,
  });

  // When bestFit returns a neighborhood, use reverseGeocode to get the parent city
  // IMPORTANT: Only update resolvedCityArea when we have new data, don't clear it when
  // data becomes undefined due to border being ON (we want to preserve the detected area)
  useEffect(() => {
    // Don't clear resolved area when data is missing - preserve it for when border is ON
    if (!data || !mapBounds) {
      return;
    }

    // If the area is a neighborhood, find the parent city
    // Neighborhood types: 'neighborhood', 'subneighborhood'
    const isNeighborhood = data.type === 'neighborhood' || data.type === 'subneighborhood';

    if (isNeighborhood) {
      // Calculate center of map bounds for reverseGeocode
      const centerLat = (mapBounds.northLat + mapBounds.southLat) / 2;
      const centerLng = (mapBounds.eastLng + mapBounds.westLng) / 2;

      // Use reverseGeocode to get hierarchical areas
      reverseGeocode(centerLat, centerLng)
        .then((response) => {
          // areas[0] = state, areas[1] = city, areas[2+] = neighborhoods
          // Prefer the city area (index 1)
          const cityArea = response.areas?.[1];
          if (cityArea && cityArea.type === 'city') {
            setResolvedCityArea(cityArea);
          } else {
            // Fallback to bestFit result if no city found
            setResolvedCityArea(data);
          }
          return undefined;
        })
        .catch((error) => {
          console.error('Failed to resolve city area:', error);  
          // Fallback to bestFit result on error
          setResolvedCityArea(data);
        });
    } else {
      // Not a neighborhood, use bestFit result directly
      setResolvedCityArea(data);
    }
  }, [data, mapBounds]);

  // Return the resolved city area if available, otherwise the raw bestFit result
  // Preserve the detected area even when border is ON (don't clear it)
  const detectedArea = resolvedCityArea ?? data;

  return {
    detectedArea, // Always return, even when border is ON
    isDetecting: shouldFetchBestFit && isLoading,
    hasMovedOnce,
  };
}
